import { useState } from 'react';

export interface DraftQuestion {
  id: string;
  name: string;
  weight: number;
  question: string;
  eval_criteria: string;
  competency_id?: string | null;
  is_compatibility?: boolean;
}

interface Competency {
  id: string;
  name: string;
}

interface GeneratedQuestionsModalProps {
  drafts: DraftQuestion[];
  competencies: Competency[];
  /** Сумма весов уже сохранённых вопросов — чтобы показать, что получится после добавления */
  currentWeight: number;
  onCancel: () => void;
  onAdd: (questions: DraftQuestion[]) => void;
}

export default function GeneratedQuestionsModal({
  drafts,
  competencies,
  currentWeight,
  onCancel,
  onAdd,
}: GeneratedQuestionsModalProps) {
  const [items, setItems] = useState<DraftQuestion[]>(drafts);

  const update = (id: string, field: keyof DraftQuestion, value: string | number | boolean | null) => {
    setItems((prev) => prev.map((q) => (q.id === id ? { ...q, [field]: value } : q)));
  };

  const drop = (id: string) => setItems((prev) => prev.filter((q) => q.id !== id));

  const addedWeight = items.reduce((s, q) => s + (Number(q.weight) || 0), 0);
  const totalAfter = currentWeight + addedWeight;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4 md:p-8">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <div>
            <h3 className="text-base font-heading font-semibold text-foreground-900">
              AI подготовил {items.length === 1 ? 'вопрос' : `вопросов: ${items.length}`}
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              Прочитайте и поправьте — в анкету попадёт только то, что вы добавите
            </p>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer"
            aria-label="Закрыть"
          >
            <i className="ri-close-line text-foreground-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {items.length === 0 && (
            <p className="text-sm text-foreground-500 text-center py-6">
              Все предложенные вопросы отклонены — добавлять нечего.
            </p>
          )}

          {items.map((q) => (
            <div key={q.id} className="bg-background-50 rounded-xl border border-background-200 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <input
                  value={q.name}
                  onChange={(e) => update(q.id, 'name', e.target.value)}
                  className="flex-1 font-medium text-foreground-900 bg-transparent border-b border-transparent hover:border-background-300 focus:border-primary-400 focus:outline-none transition-colors text-sm py-0.5"
                />
                <div className="flex items-center gap-1.5 shrink-0">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={q.weight}
                    onChange={(e) => update(q.id, 'weight', Number(e.target.value))}
                    className="w-16 px-2 py-1 rounded bg-white border border-background-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <span className="text-xs text-foreground-400">%</span>
                  <button
                    onClick={() => drop(q.id)}
                    title="Не добавлять этот вопрос"
                    className="w-7 h-7 rounded hover:bg-red-50 flex items-center justify-center text-foreground-300 hover:text-red-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-foreground-500 mb-1 block">Вопрос кандидату</label>
                <textarea
                  value={q.question}
                  onChange={(e) => update(q.id, 'question', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg bg-white border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
                />
              </div>

              <div>
                <label className="text-xs text-foreground-500 mb-1 block">Критерии оценки для AI</label>
                <textarea
                  value={q.eval_criteria}
                  onChange={(e) => update(q.id, 'eval_criteria', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg bg-white border border-background-200 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
                />
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <label className="text-xs text-foreground-500 mb-1 block">Какой навык проверяет</label>
                  <select
                    value={q.competency_id || ''}
                    onChange={(e) => update(q.id, 'competency_id', e.target.value)}
                    className="px-3 py-1.5 rounded-lg bg-white border border-background-200 text-xs focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                  >
                    <option value="">— не привязан —</option>
                    {competencies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer select-none mt-4">
                  <input
                    type="checkbox"
                    checked={!!q.is_compatibility}
                    onChange={(e) => update(q.id, 'is_compatibility', e.target.checked)}
                    className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                  />
                  Вопрос на совместимость с руководителем
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-background-200 flex flex-wrap items-center justify-between gap-3">
          <p className={`text-xs ${totalAfter === 100 ? 'text-foreground-500' : 'text-amber-700'}`}>
            Сумма весов вопросов после добавления: {totalAfter}%
            {totalAfter !== 100 && ' — должна быть ровно 100%, поправьте веса здесь или в анкете'}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2.5 rounded-xl text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            >
              Отмена
            </button>
            <button
              onClick={() => onAdd(items)}
              disabled={items.length === 0}
              className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-add-line" />
              Добавить в анкету
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
